import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import AHDate from "_shared/dates/date";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import Name from "_shared/names/name";
import Title from "_shared/titles/title";
import UserOpenEntityAgreement from "legal-entities/agreements/open/user-open-entity-agreement";
import Individual from "marketplace/individuals/individual";
import UserInformationSchema, {
  UserInformationJSON,
  UserInformationParams,
} from "users/information/_schemas/user-information-schema";
import type { UserOnboardingWork } from "users/information/user-onboarding-work";

export default class UserInformation {
  public static readonly EXAMPLE: UserInformation = UserInformation.create({
    id: Guid.generate(),
    hasSigningAuthority: false,
    hasEntityWideNotificationsAccess: false,
    hasMemberPermissionsAccess: false,
    viewingAsVendor: false,
    email: EmailAddress.create("test@example.com"),
    name: Name.create({ params: { firstName: "Test", lastName: "User" } }),
    title: Title.create("Example Title"),
    legalEntityId: Guid.generate(),
    openEntityAgreements: [UserOpenEntityAgreement.TEST_AGREEMENT],
    hasOpenEntityAgreements: false,
    isSuperUser: false,
    avatarId: Guid.generate(),
    companyId: Guid.generate(),
    companyName: "Example Company",
    companyProfileVisible: true,
    teamsOn: 3,
    teamsLeading: 1,
    myComments: "This is a test user.",
    onboardingWorkId: Guid.generate(),
    onboardingWork: undefined,
    connectionId: Guid.generate(),
    createdDate: new AHDate(),
    individualId: Guid.generate(),
  });

  public static create(values: UserInformationParams): UserInformation {
    const parsed = UserInformationSchema.validate(values);
    return new UserInformation(parsed);
  }

  public static fromJSON(json: UserInformationJSON | null | undefined): UserInformation | undefined {
    if (isNullOrUndefined(json)) return undefined;
    const params = UserInformationSchema.deserialize(json);
    return new UserInformation(params);
  }

  public static fromJSONArray(jsonArray: (UserInformationJSON | null | undefined)[]): UserInformation[] {
    if (isNullOrUndefined(jsonArray)) return [];
    return jsonArray
      .map((json) => UserInformation.fromJSON(json))
      .filter((user): user is UserInformation => !isNullOrUndefined(user));
  }

  public readonly id: Guid;
  public readonly isSuperUser?: boolean;
  public readonly hasSigningAuthority?: boolean;
  /**
   * Whether this user holds an entity-wide notifications grant (issue #4438). Read/write access
   * to one's own notifications is inherent and is deliberately not what this reports.
   */
  public readonly hasEntityWideNotificationsAccess?: boolean;
  /**
   * Whether this user may read every grant in their organization (issue #6785). The Ably token
   * carries the organization's permissions channel only for such a user, so the app reads this
   * to avoid asking for a channel it would be refused; it is not itself the gate.
   */
  public readonly hasMemberPermissionsAccess?: boolean;
  public readonly viewingAsVendor?: boolean;
  public readonly email?: EmailAddress;
  public readonly name: Name;
  public readonly title?: Title;
  public readonly openEntityAgreements: UserOpenEntityAgreement[];
  public readonly hasOpenEntityAgreements: boolean;
  public readonly individualId?: Guid;
  public readonly connectionId?: Guid;
  public readonly createdDate?: AHDate;
  public readonly companyId?: Guid;
  public readonly legalEntityId: Guid;
  public readonly companyName?: string;
  public readonly companyProfileVisible?: boolean;
  public readonly avatarId?: Guid;
  public readonly teamsOn?: number;
  public readonly teamsLeading?: number;
  public readonly myComments?: string;
  public readonly onboardingWorkId?: Guid;
  public readonly onboardingWork?: UserOnboardingWork;

  public constructor(params: UserInformationParams) {
    this.id = params.id;
    this.hasSigningAuthority = params.hasSigningAuthority;
    this.hasEntityWideNotificationsAccess = params.hasEntityWideNotificationsAccess;
    this.hasMemberPermissionsAccess = params.hasMemberPermissionsAccess;
    this.viewingAsVendor = params.viewingAsVendor;
    this.email = params.email;
    this.name = params.name;
    this.title = params.title;
    this.legalEntityId = params.legalEntityId;
    this.openEntityAgreements = this.mapOpenEntityAgreements(params.openEntityAgreements);
    this.hasOpenEntityAgreements = params.hasOpenEntityAgreements ?? false;
    this.individualId = params.individualId;
    this.isSuperUser = params.isSuperUser;
    this.connectionId = params.connectionId;
    this.createdDate = params.createdDate;
    this.companyId = params.companyId;
    this.companyName = params.companyName ?? undefined;
    this.companyProfileVisible = params.companyProfileVisible;
    this.avatarId = params.avatarId;
    this.teamsOn = params.teamsOn;
    this.teamsLeading = params.teamsLeading;
    this.myComments = params.myComments;
    this.onboardingWorkId = params.onboardingWorkId;
    this.onboardingWork = params.onboardingWork;
  }

  public toJSON(): UserInformationJSON {
    return UserInformationSchema.serialize(this);
  }

  public isEqualTo(other: UserInformation): boolean {
    return this.id.isEqualTo(other?.id);
  }

  public toIndividual(): Individual | undefined {
    if (isNullOrUndefined(this.individualId)) {
      console.warn("UserInformation.toIndividual: individualId is null or undefined.");
      return;
    }
    if (isNullOrUndefined(this.legalEntityId)) {
      console.warn("UserInformation.toIndividual: legalEntityId is null or undefined.");
      return;
    }
    if (isNullOrUndefined(this.name.firstName) || isNullOrUndefined(this.name.lastName)) {
      console.warn("UserInformation.toIndividual: name is incomplete.");
      return;
    }

    return Individual.create({
      ...this,
      id: this.individualId,
      userId: this.id,
      isVisible: true,
      legalEntityId: this.legalEntityId,
      profile: {
        firstName: this.name.firstName,
        lastName: this.name.lastName,
        avatarId: this.avatarId,
        email: this.email,
      },
    });
  }

  public toString(): string {
    return `UserInformation (
      id: ${this.id.value},
      viewingAsVendor: ${this.viewingAsVendor},
      email: ${this.email?.toString()},
      name: ${this.name.toString()},
      title: ${this.title}
      openEntityAgreements: [${this.openEntityAgreements.map((a) => a.toString()).join(", ")}],
      hasOpenEntityAgreements: ${this.hasOpenEntityAgreements},
      individualId: ${this.individualId},
      isSuperUser: ${this.isSuperUser},
      connectionId: ${this.connectionId},
      createdDate: ${this.createdDate},
      companyId: ${this.companyId},
      companyName: ${this.companyName},
      companyProfileVisible: ${this.companyProfileVisible},
      avatarId: ${this.avatarId},
      teamsOn: ${this.teamsOn},
      teamsLeading: ${this.teamsLeading},
      myComments: ${this.myComments},
      onboardingWorkId: ${this.onboardingWorkId}
    )`;
  }

  private mapOpenEntityAgreements(agreements?: UserOpenEntityAgreement[]): UserOpenEntityAgreement[] {
    if (!agreements) return [];
    return agreements.map((agreement) =>
      UserOpenEntityAgreement.create({
        id: agreement.id,
        entityName: agreement.entityName,
        entityLocation: agreement.entityLocation,
        onboardingTemplateId: agreement.onboardingTemplateId,
        onboardingTemplateVersion: agreement.onboardingTemplateVersion,
        workflowVersion: agreement.workflowVersion,
      }),
    );
  }
}
